import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Printer, Calendar, Clock, MapPin, Truck, User, Mail, Phone, Package, RefreshCw, AlertCircle, XCircle, Loader2, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function formatSlotTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
}

export default function BookingReceipt() {
  const { id } = useParams();
  const { customer, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ date: "", time: "" });
  const [rescheduling, setRescheduling] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const getToken = () => localStorage.getItem("mahra_session_token");

  useEffect(() => {
    if (!authLoading && !customer) navigate("/login", { state: { from: `/account/bookings/${id}` }, replace: true });
    if (customer) fetchReceipt();
  }, [customer, authLoading, id, navigate]);

  const fetchReceipt = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/customer/bookings/${id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        // Fetch appointments for this booking
        const apptRes = await axios.get(`${API}/api/appointments/my?booking_id=${id}`);
        setAppointments(apptRes.data.appointments || []);
      } else navigate("/account/bookings", { replace: true });
    } catch {} finally { setLoading(false); }
  };

  const canReschedule = (booking) => {
    const endDate = booking.return_by_date || booking.event_date;
    if (!endDate) return true;
    const end = new Date(endDate + "T00:00:00");
    const now = new Date();
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 2;
  };

  const openReschedule = async (appt) => {
    setRescheduleAppt(appt);
    setNewSlot({ date: "", time: "" });
    setLoadingSlots(true);
    try {
      const b = data.booking;
      const eventDate = b.event_date;
      if (eventDate) {
        const res = await axios.get(`${API}/api/appointments/available-slots?appointment_type=${appt.appointment_type}&event_date=${eventDate}`);
        setAvailableSlots(res.data.dates || []);
      }
    } catch {} finally { setLoadingSlots(false); }
  };

  const handleReschedule = async () => {
    if (!newSlot.date || !newSlot.time) { toast.error("Please select a new time slot"); return; }
    setRescheduling(true);
    try {
      const token = getToken();
      await axios.put(`${API}/api/customer/appointments/${rescheduleAppt.id}/reschedule`, {
        date: newSlot.date, time: newSlot.time,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Appointment rescheduled successfully");
      setRescheduleAppt(null);
      const apptRes = await axios.get(`${API}/api/appointments/my?booking_id=${id}`);
      setAppointments(apptRes.data.appointments || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reschedule");
    } finally { setRescheduling(false); }
  };

  const openCancelPreview = async () => {
    setLoadingCancel(true);
    setShowCancelModal(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/customer/bookings/${id}/cancel-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCancelPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot preview cancellation");
      setShowCancelModal(false);
    } finally { setLoadingCancel(false); }
  };

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/api/customer/bookings/${id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Booking cancelled. Refund of $${res.data.refund_amount?.toFixed(2)} will be processed.`);
      setShowCancelModal(false);
      fetchReceipt();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancellation failed");
    } finally { setCancelling(false); }
  };

  if (authLoading || !customer || loading || !data) return null;

  const b = data.booking;
  const outfit = data.outfit;
  const eventDate = b.event_date ? new Date(b.event_date + "T00:00:00") : null;
  const allowReschedule = canReschedule(b);
  const pickupAppt = appointments.find(a => a.appointment_type === "pickup");
  const dropoffAppt = appointments.find(a => a.appointment_type === "dropoff");

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-[700px] mx-auto px-4 lg:px-8 py-10 lg:py-16">
        <div className="flex items-center justify-between mb-8 print:hidden">
          <Link to="/account/bookings" className="flex items-center gap-1.5 text-[11px] text-[#676664]/50 tracking-[0.1em] uppercase hover:text-[#676664]">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Bookings
          </Link>
          <Button
            onClick={() => window.print()}
            variant="outline"
            className="text-[11px] tracking-[0.1em] uppercase border-[#282828]/10"
            data-testid="print-receipt-btn"
          >
            <Printer className="w-3.5 h-3.5 mr-2" /> Print Receipt
          </Button>
        </div>

        {/* Receipt Card */}
        <div className="bg-white border border-[#282828]/8 print:border-0 print:shadow-none" data-testid="booking-receipt">
          {/* Header */}
          <div className="p-6 lg:p-8 border-b border-[#282828]/5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-lg text-[#282828] font-light">Booking Confirmation</p>
              <span className="text-[9px] tracking-[0.1em] uppercase px-2.5 py-1 bg-[#9A7B4F]/10 text-[#9A7B4F]">{b.booking_status}</span>
            </div>
            <p className="text-[10px] text-[#676664]/40">Booking ID: {b.id}</p>
            {b.created_at && <p className="text-[10px] text-[#676664]/40">Booked: {format(new Date(b.created_at), "MMM d, yyyy 'at' h:mm a")}</p>}
          </div>

          {/* Outfit Info */}
          <div className="p-6 lg:p-8 border-b border-[#282828]/5 flex gap-4">
            {outfit?.images?.[0] && (
              <img src={outfit.images[0]} alt="" className="w-20 h-24 object-cover flex-shrink-0 print:w-16 print:h-20" />
            )}
            <div>
              <p className="text-[10px] text-[#9A7B4F] tracking-[0.15em] uppercase">{outfit?.category}</p>
              <p className="text-[14px] text-[#282828] mt-0.5">{b.outfit_name}</p>
              {b.rental_option_name && <p className="text-[11px] text-[#676664]/60 mt-0.5">{b.rental_option_name}</p>}
            </div>
          </div>

          {/* Schedule */}
          <div className="p-6 lg:p-8 border-b border-[#282828]/5">
            <h3 className="text-[10px] text-[#676664]/40 tracking-[0.15em] uppercase mb-3">Rental Schedule</h3>
            <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-[12px]">
              <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#9A7B4F]" /><div><p className="text-[#676664]/50 text-[10px]">Event Date</p><p className="text-[#282828]">{eventDate ? format(eventDate, "EEE, MMM d, yyyy") : "—"}</p></div></div>
              <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#9A7B4F]" /><div><p className="text-[#676664]/50 text-[10px]">Duration</p><p className="text-[#282828]">{b.rental_days} days</p></div></div>
              {b.delivery_date && <div className="flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-[#9A7B4F]" /><div><p className="text-[#676664]/50 text-[10px]">Dispatch</p><p className="text-[#282828]">{format(new Date(b.delivery_date + "T00:00:00"), "EEE, MMM d")}</p></div></div>}
              {b.return_by_date && <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-[#9A7B4F]" /><div><p className="text-[#676664]/50 text-[10px]">Return By</p><p className="text-[#282828]">{format(new Date(b.return_by_date + "T00:00:00"), "EEE, MMM d")}</p></div></div>}
            </div>
          </div>

          {/* Appointments */}
          {(pickupAppt || dropoffAppt) && (
            <div className="p-6 lg:p-8 border-b border-[#282828]/5 print:hidden" data-testid="booking-appointments-section">
              <h3 className="text-[10px] text-[#676664]/40 tracking-[0.15em] uppercase mb-3">Appointments</h3>
              <div className="space-y-3">
                {pickupAppt && (
                  <div className="flex items-center justify-between" data-testid="receipt-pickup-appt">
                    <div>
                      <p className="text-[10px] text-[#676664]/50">Collection / Delivery</p>
                      <p className="text-[12px] text-[#282828]">{pickupAppt.date} at {formatSlotTime(pickupAppt.time)}</p>
                    </div>
                    {allowReschedule ? (
                      <button onClick={() => openReschedule(pickupAppt)} className="text-[10px] text-[#9A7B4F] tracking-[0.1em] uppercase hover:underline flex items-center gap-1" data-testid="reschedule-pickup-btn">
                        <RefreshCw className="w-3 h-3" /> Reschedule
                      </button>
                    ) : (
                      <span className="text-[10px] text-[#676664]/40 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Contact us to change</span>
                    )}
                  </div>
                )}
                {dropoffAppt && (
                  <div className="flex items-center justify-between" data-testid="receipt-dropoff-appt">
                    <div>
                      <p className="text-[10px] text-[#676664]/50">Return / Drop-off</p>
                      <p className="text-[12px] text-[#282828]">{dropoffAppt.date} at {formatSlotTime(dropoffAppt.time)}</p>
                    </div>
                    {allowReschedule ? (
                      <button onClick={() => openReschedule(dropoffAppt)} className="text-[10px] text-[#9A7B4F] tracking-[0.1em] uppercase hover:underline flex items-center gap-1" data-testid="reschedule-dropoff-btn">
                        <RefreshCw className="w-3 h-3" /> Reschedule
                      </button>
                    ) : (
                      <span className="text-[10px] text-[#676664]/40 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Contact us to change</span>
                    )}
                  </div>
                )}
              </div>
              {!allowReschedule && (
                <div className="mt-3 pt-3 border-t border-[#282828]/5">
                  <p className="text-[11px] text-[#676664]/60">
                    The rescheduling window has closed. Please <a href="mailto:hello@shopmahra.com.au" className="text-[#9A7B4F] underline">email us</a> or use our <Link to="/contact" className="text-[#9A7B4F] underline">contact form</Link> to request changes.
                  </p>
                </div>
              )}
              {allowReschedule && (
                <p className="text-[10px] text-[#676664]/40 mt-3">You can reschedule up to 2 days before your booking ends.</p>
              )}
            </div>
          )}

          {/* Customer */}
          <div className="p-6 lg:p-8 border-b border-[#282828]/5">
            <h3 className="text-[10px] text-[#676664]/40 tracking-[0.15em] uppercase mb-3">Customer Details</h3>
            <div className="space-y-2 text-[12px]">
              <p className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-[#676664]/30" />{b.customer_name}</p>
              <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#676664]/30" />{b.customer_email}</p>
              <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#676664]/30" />{b.customer_phone}</p>
              {b.delivery_method && <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#676664]/30" />{b.delivery_method === "pickup" ? "Local Pickup — Oakleigh South" : `Delivery — ${b.delivery_address || ""}`}</p>}
            </div>
          </div>

          {/* Cost Summary */}
          <div className="p-6 lg:p-8">
            <h3 className="text-[10px] text-[#676664]/40 tracking-[0.15em] uppercase mb-3">Payment Summary</h3>
            <div className="space-y-2 text-[12px]">
              <div className="flex justify-between"><span className="text-[#676664]/60">Rental Fee ({b.rental_days} days)</span><span>${b.rental_cost?.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#676664]/60">Refundable Deposit</span><span>${b.deposit_amount?.toFixed(2)}</span></div>
              {b.delivery_fee > 0 && <div className="flex justify-between"><span className="text-[#676664]/60">Delivery</span><span>${b.delivery_fee?.toFixed(2)}</span></div>}
              {b.refund_amount > 0 && (
                <div className="flex justify-between text-[#4A7C59]"><span>Refunded</span><span>-${b.refund_amount?.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between pt-3 border-t border-[#282828]/10 font-medium">
                <span className="text-[#282828]">Total Charged</span>
                <span className="text-[#9A7B4F] text-sm">${b.total_amount?.toFixed(2)}</span>
              </div>
            </div>
            {b.booking_status !== "cancelled" && (
              <p className="text-[9px] text-[#676664]/40 mt-4 font-light">
                Your security deposit of ${b.deposit_amount?.toFixed(2)} will be refunded within 7 business days after the outfit is returned in good condition.
              </p>
            )}
          </div>

          {/* Cancel Booking Button */}
          {b.booking_status !== "cancelled" && b.booking_status !== "dispatched" && b.payment_status === "paid" && (
            <div className="p-6 lg:p-8 border-t border-[#282828]/5 print:hidden">
              <button
                onClick={openCancelPreview}
                className="flex items-center gap-2 text-[11px] text-red-500/70 hover:text-red-600 tracking-[0.1em] uppercase transition-colors"
                data-testid="cancel-booking-btn"
              >
                <XCircle className="w-3.5 h-3.5" /> Cancel Booking
              </button>
            </div>
          )}
          {b.booking_status === "cancelled" && (
            <div className="p-6 lg:p-8 border-t border-red-100 bg-red-50/30">
              <p className="text-[11px] text-red-600/70">This booking was cancelled{b.cancelled_at ? ` on ${format(new Date(b.cancelled_at), "MMM d, yyyy")}` : ""}.</p>
              {b.refund_amount > 0 && <p className="text-[11px] text-[#676664]/60 mt-1">Refund of ${b.refund_amount?.toFixed(2)} has been processed.</p>}
            </div>
          )}

          {/* Re-book Button */}
          {["completed", "cancelled"].includes(b.booking_status) && b.outfit_id && (
            <div className="p-6 lg:p-8 border-t border-[#282828]/5 print:hidden" data-testid="rebook-section">
              <Link
                to={`/outfit/${outfit?.slug || b.outfit_id}`}
                className="inline-flex items-center gap-2 text-[11px] text-[#9A7B4F] tracking-[0.15em] uppercase hover:text-[#8B6B3F] transition-colors"
                data-testid="rebook-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Re-book This Outfit
              </Link>
              <p className="text-[10px] text-[#676664]/40 mt-1.5">Book this outfit again with new dates and options.</p>
            </div>
          )}
        </div>

        {/* Reschedule Modal */}
        {rescheduleAppt && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" data-testid="reschedule-modal">
            <div className="bg-white w-full max-w-md max-h-[80vh] overflow-y-auto border border-[#282828]/10">
              <div className="p-5 border-b border-[#282828]/8">
                <h3 className="text-sm text-[#282828] font-medium">Reschedule {rescheduleAppt.appointment_type === "pickup" ? "Collection / Delivery" : "Return / Drop-off"}</h3>
                <p className="text-[11px] text-[#676664]/50 mt-1">Currently: {rescheduleAppt.date} at {formatSlotTime(rescheduleAppt.time)}</p>
              </div>
              <div className="p-5 space-y-3">
                {loadingSlots ? (
                  <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#9A7B4F] border-t-transparent rounded-full animate-spin" /></div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-[12px] text-[#676664]/50 text-center py-4">No alternative slots available</p>
                ) : (
                  availableSlots.map((dateGroup) => (
                    <div key={dateGroup.date} className="border border-[#282828]/8">
                      <div className="px-4 py-2 border-b border-[#282828]/6 bg-[#FAF8F0]">
                        <span className="text-[11px] text-[#282828] font-medium">{dateGroup.day_label}</span>
                      </div>
                      <div className="p-3 grid grid-cols-3 gap-2">
                        {dateGroup.times.map((time) => {
                          const isSelected = newSlot.date === dateGroup.date && newSlot.time === time;
                          return (
                            <button
                              type="button"
                              key={time}
                              onClick={() => setNewSlot({ date: dateGroup.date, time })}
                              className={`py-2 px-1.5 text-[11px] border transition-all ${isSelected
                                ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]'
                                : 'bg-white text-[#5A5A5A] border-[#282828]/12 hover:border-[#9A7B4F]/40'}`}
                              data-testid={`reschedule-slot-${dateGroup.date}-${time}`}
                            >
                              {formatSlotTime(time)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="p-5 border-t border-[#282828]/8 flex gap-3">
                <Button variant="outline" onClick={() => setRescheduleAppt(null)} className="flex-1 text-[11px] border-[#282828]/10" data-testid="cancel-reschedule-btn">Cancel</Button>
                <Button
                  onClick={handleReschedule}
                  disabled={!newSlot.date || !newSlot.time || rescheduling}
                  className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px]"
                  data-testid="confirm-reschedule-btn"
                >
                  {rescheduling ? "Rescheduling..." : "Confirm Reschedule"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Cancel Booking Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" data-testid="cancel-booking-modal">
            <div className="bg-white w-full max-w-md border border-[#282828]/10">
              <div className="p-5 border-b border-[#282828]/8">
                <h3 className="text-sm text-[#282828] font-medium flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" /> Cancel Booking
                </h3>
              </div>
              <div className="p-5">
                {loadingCancel ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
                ) : cancelPreview && !cancelPreview.can_cancel ? (
                  <div>
                    <p className="text-[13px] text-[#282828] mb-2">Unable to Cancel</p>
                    <p className="text-[12px] text-[#676664]/70 leading-relaxed">{cancelPreview.reason}</p>
                  </div>
                ) : cancelPreview ? (
                  <div className="space-y-4">
                    <div className="bg-[#FAF8F0] border border-[#282828]/8 p-4">
                      <p className="text-[11px] text-[#282828] font-medium mb-2">Cancellation Policy</p>
                      <p className="text-[12px] text-[#676664]/70 leading-relaxed">{cancelPreview.policy}</p>
                    </div>
                    <div className="space-y-2 text-[12px]">
                      <p className="text-[10px] text-[#676664]/40 tracking-[0.15em] uppercase">Refund Breakdown</p>
                      <div className="flex justify-between">
                        <span className="text-[#676664]/60">Rental Fee</span>
                        <span>{cancelPreview.breakdown.rental_refund > 0
                          ? <span className="text-[#4A7C59]">${cancelPreview.breakdown.rental_refund.toFixed(2)} refund</span>
                          : <span className="text-red-500/70">$0.00 (non-refundable)</span>}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#676664]/60">Security Deposit</span>
                        <span className="text-[#4A7C59]">${cancelPreview.breakdown.deposit_refund.toFixed(2)} refund</span>
                      </div>
                      {cancelPreview.breakdown.delivery_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-[#676664]/60">Delivery Fee</span>
                          <span>{cancelPreview.breakdown.delivery_refund > 0
                            ? <span className="text-[#4A7C59]">${cancelPreview.breakdown.delivery_refund.toFixed(2)} refund</span>
                            : <span className="text-red-500/70">$0.00 (non-refundable)</span>}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-[#282828]/8 font-medium">
                        <span className="text-[#282828]">Total Refund</span>
                        <span className="text-[#4A7C59]">${cancelPreview.total_refund.toFixed(2)} AUD</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-red-500/70 leading-relaxed">
                      This action cannot be undone. Your refund will be processed to your original payment method within 5-7 business days.
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="p-5 border-t border-[#282828]/8 flex gap-3">
                <Button variant="outline" onClick={() => { setShowCancelModal(false); setCancelPreview(null); }} className="flex-1 text-[11px] border-[#282828]/10" data-testid="cancel-modal-close-btn">
                  Keep Booking
                </Button>
                {cancelPreview?.can_cancel && (
                  <Button
                    onClick={confirmCancel}
                    disabled={cancelling}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white text-[11px]"
                    data-testid="confirm-cancel-btn"
                  >
                    {cancelling ? "Cancelling..." : "Confirm Cancellation"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
